import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import _, {isNil} from 'lodash';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import clsx from 'clsx';
import {v4} from 'uuid';
import StandardReports from '../../StandardReports';
import {
  ColumnConfig,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import {SourceItem} from '../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import ArrDocMaxLimitWarning from '../../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  useAdditionMessageToBackground,
  useBatchReportExportToExcel,
  useGetABMDocJSONResultData,
  useGetABMJSONResultData,
  useGetABMViewData,
  useGetDocumentMasterDocId,
  useGetReviewResultsSources,
  useLeftPanelReport,
  useSaveABMReportData,
} from '../../../../../hooks/reporting/useReporting';
import {
  getActionMenuOptions,
  assignmentColumns,
  BatchLevel,
  documentColumns,
  getSaveABMReportDataPayload,
  mergeApiDataIntoConfig,
  updateApplyButtonState,
  getDocumentListFilterOptions,
  getColumnSortingKey,
  getDefaultSortingForBatchLevel,
  getManageBatchTypedOptions,
  sourcesSectionKeys,
  getABMJSONResultReqPayload,
  getSourceDetailForAction,
  getBulkActionExecutePayload,
  getRedistributeActionData,
  convertServerBatchTypeToClient,
  mapInSetCountKeyToGridField,
  getAppliedFilterValueObject,
} from './Helper';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import './AdvancedBatchManagementContainer.scss';
import DocCountSummaryView from './component/DocCountSummaryView/DocCountSummaryView';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import {Typography} from '@mui/material';
import {
  BgNavigationTypes,
  PAGE_SIZE,
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  SourceTypeContextCode,
  ToastType,
} from '../../../../../shared/constants';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../shared/types';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  ABMDocResultItemType,
  GetABMDocJSONResultReqPayload,
  GetABMJSONResultReqPayload,
  GetDocumentMasterDocIdResponse,
} from '../../../../../api/types/reportingApi.type';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import ActionMenuWrapper from '../../../../action-menu/action-menu-wrapper/ActionMenuWrapper';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';
import {
  storeDocumentListMasterId,
  storeNavigationSourceTypeCode,
  storeSourceTypeCode,
} from '../../../../../redux/slice/docViewSlice';
import {clearDocViewState} from '../../../../document-viewer/docViewerUtils';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../redux/slice/docListSlice';
import {RootState} from '../../../../../redux/store';

dayjs.extend(utc);

const stringPath = 'reports.advanceBatchManagement.';
const DEFAULT_PAGE_SIZE = PAGE_SIZE;

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}

const AdvancedBatchManagementContainer: React.FC = () => {
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isResetExecuted = useRef<boolean>(false);
  const initialJsonConfig = useRef<any | null>(null);
  const loadedReportIdFromUrl = useRef<number | null>(null);

  const [selectedBatchType, setSelectedBatchType] = useState<{
    id: BatchLevel;
    label: string;
  }>({
    id: 'Assignment',
    label: t(stringPath + 'assignmentType'),
  });

  const [savedReportData, setSavedReportData] = useState<{
    reportId: number;
    batchLevel: BatchLevel;
    notificationId?: number;
  }>({reportId: 0, notificationId: 0, batchLevel: 'Assignment'});

  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});

  const [reportConfig, setReportConfig] = useState<any | null>(null);

  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      sort: getDefaultSortingForBatchLevel(selectedBatchType.id),
    });

  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);

  const [isQueryRun, setIsQueryRun] = useState(false);

  const [documentFilterType, setDocumentFilterType] = useState<ISelectItem>(
    getDocumentListFilterOptions(t)[0]
  );

  const [reportGridData, setReportGridData] = useState<any[]>([]);

  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const [selectedDocIds, setSelectedDocIds] = useState<
    Map<string, ABMDocResultItemType>
  >(new Map());

  const [unSelectedDocIds, setUnSelectedDocIds] = useState<string[]>([]);

  const [isAllDocSelected, setIsAllDocSelected] = useState<boolean>(false);

  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<
    Map<string, string[]>
  >(new Map());

  const [selectedDocsCount, setSelectedDocsCount] = useState<number>(0);
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [showDocLimitDialog, setShowDocLimitDialog] = useState<boolean>(false);
  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>();
  const [isDocViewLoading, setIsDocViewLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const [docListMasterId, setDocListMasterId] = useState<number>(0);

  const [searchParams] = useSearchParams();

  /** Get Left Panel Report Data Request when saved report is opened */
  const reportIdFromUrl = useMemo(() => {
    const key = location.pathname.split('/')[2];
    if (key === 'batch-report') {
      const params = new URLSearchParams(location.search);
      const id =
        params.get('ReportID') ??
        params.get('reportId') ??
        params.get('ReportId') ??
        params.get('reportID') ??
        '';
      const parsed = Number(id);
      sessionStorage.setItem('reportID', id);
      return Number.isFinite(parsed) ? parsed : 0;
    } else {
      const reportId = searchParams.get('ReportID');
      sessionStorage.setItem('reportID', reportId ? reportId.toString() : '0');
      const parsed = Number(reportId);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }, [location.pathname, location.search, searchParams]);

  const {
    data: reviewResultsSources,
    isLoading: isLoadingReviewResultsSources,
    isError: isErrorReviewResultsSources,
    error: reviewResultSourcesError,
    refetch: fetchReviewResultsSources,
  } = useGetReviewResultsSources(reportIdFromUrl);

  const {
    mutateAsync: saveABMReportDataRequest,
    isPending: isSavingABMReportData,
  } = useSaveABMReportData();

  const {
    refetch: refetchABMSummaryViewData,
    data: abmViewData,
    isLoading: isLoadingABMViewData,
  } = useGetABMViewData({
    reportId: savedReportData.reportId
      ? savedReportData.reportId
      : reportIdFromUrl,
    batchType: savedReportData.batchLevel,
  });

  /** Get ABM Assignment Result Data Request */
  const {
    mutate: getABMJSONResultData,
    data: abmJSONResultData,
    isPending: isLoadingABMJSONResultData,
    isError: isErrorABMJSONResultData,
  } = useGetABMJSONResultData();

  /** Get ABM Document Result Data Request */
  const {
    mutate: getABMDocJSONResultData,
    data: abmDocJSONResultData,
    isPending: isLoadingABMDocJSONResultData,
    isError: isErrorABMDocJSONResultData,
  } = useGetABMDocJSONResultData();

  /** For generate doc list Id for open document in doc view or list */
  const {mutate: getDocumentMasterDocId} = useGetDocumentMasterDocId();

  const {mutate: additionMessageToBackground} =
    useAdditionMessageToBackground();

  const {
    data: leftPanelSavedReportData,
    isLoading: isLoadingSavedLeftPanelReport,
    isError: isErrorSavedLeftPanelReport,
    error: leftPanelSavedReportError,
    refetch: fetchSavedLeftPanelReportData,
  } = useLeftPanelReport(reportIdFromUrl, false);

  // Export report api call
  const {
    mutate: batchReportExportToExcel,
    isPending: isBatchReportExportToExcelLoading,
  } = useBatchReportExportToExcel();

  const reportKey = useMemo(() => {
    const key = location.pathname.split('/')[2];
    if (key === 'batch-report') {
      return 'AdvancedBatchManagementReport';
    }
    return key;
  }, [location.pathname]);

  useEffect(() => {
    if (abmJSONResultData?.data && isLoadingABMJSONResultData === false) {
      setReportGridData(
        (abmJSONResultData.data ?? []).map((item: any) => {
          return {
            id: `${v4()}_${item.assignmentID}`,
            ...item,
          };
        })
      );
    } else if (isErrorABMJSONResultData && !isLoadingABMJSONResultData) {
      setReportGridData([]);
    }
  }, [abmJSONResultData, isLoadingABMJSONResultData]);

  useEffect(() => {
    if (abmDocJSONResultData?.data && isLoadingABMDocJSONResultData === false) {
      setReportGridData(
        (abmDocJSONResultData.data ?? []).map((item: any) => ({
          id: `${v4()}_${item.documentID}`,
          ...item,
        }))
      );
    } else if (isErrorABMDocJSONResultData && !isLoadingABMDocJSONResultData) {
      setReportGridData([]);
    }
  }, [abmDocJSONResultData, isLoadingABMDocJSONResultData]);

  useEffect(() => {
    if (loadedReportIdFromUrl.current === null) {
      loadedReportIdFromUrl.current = reportIdFromUrl;
      loadReportConfig().then(() => {
        if (
          (location.state?.fromReportLanding ||
            location.state?.isBackgroundReport) &&
          reportIdFromUrl !== 0
        ) {
          fetchSavedLeftPanelReportData();
        } else if (reportIdFromUrl !== 0) {
          fetchSavedLeftPanelReportData();
        } else {
          fetchReviewResultsSources();
        }
      });
    } else if (reportIdFromUrl !== 0 && loadedReportIdFromUrl !== null) {
      // id updated in url so reset all state and fetch report data again
      isResetExecuted.current = false;
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setReportConfig(null);
      setSavedReportData({
        reportId: 0,
        batchLevel: 'Assignment',
        notificationId: 0,
      });
      setDocumentFilterType(getDocumentListFilterOptions(t)[0]);
      loadedReportIdFromUrl.current = reportIdFromUrl;
      fetchSavedLeftPanelReportData();
    }
  }, [reportIdFromUrl]);

  useEffect(() => {
    if (isErrorReviewResultsSources && reviewResultSourcesError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reviewResultSourcesError as any)?.response?.data?.title ??
          t(stringPath + 'reportConfigAPIError'),
      });
    }
  }, [isErrorReviewResultsSources, reviewResultSourcesError]);

  useEffect(() => {
    if (
      !isLoadingReviewResultsSources &&
      !isErrorReviewResultsSources &&
      reviewResultsSources &&
      initialJsonConfig.current
    ) {
      setReportConfig(
        mergeApiDataIntoConfig(
          initialJsonConfig.current,
          Array.isArray(reviewResultsSources)
            ? reviewResultsSources[0]
            : reviewResultsSources,
          leftPanelSavedReportData
        )
      );
      if (
        leftPanelSavedReportData == null &&
        (location.state?.fromReportLanding ||
          location.state?.isBackgroundReport)
      )
        return;
      // To prevent result call when report id is zero or not background report opened
      // Also check if ReportLabel exists in URL when reportIdFromUrl has data
      const hasReportLabel =
        new URLSearchParams(location.search).get('ReportLabel') !== null;

      if (reportIdFromUrl === 0 || (reportIdFromUrl > 0 && hasReportLabel))
        return;

      const batchLevel = leftPanelSavedReportData?.batchLevel ?? 'ASSIGNMENT';
      const selectedBatch =
        getManageBatchTypedOptions(
          t,
          convertServerBatchTypeToClient(batchLevel)
        ).find(
          (item) => item.id === convertServerBatchTypeToClient(batchLevel)
        ) ?? getManageBatchTypedOptions(t, 'Assignment')[0];

      setSelectedBatchType({
        id: selectedBatch.id as BatchLevel,
        label: selectedBatch.label,
      });

      if (batchLevel === 'DOCUMENT') {
        setDocumentFilterType(
          getDocumentListFilterOptions(t).find(
            (item) => item.id === leftPanelSavedReportData?.filterDocumentGrid
          ) ?? getDocumentListFilterOptions(t)[0]
        );
      }

      if (location.state?.isBackgroundReport) {
        setSavedReportData({
          reportId: reportIdFromUrl,
          batchLevel: convertServerBatchTypeToClient(batchLevel),
        });
      }

      setSavedFilterValues(
        getAppliedFilterValueObject(
          Array.isArray(reviewResultsSources)
            ? reviewResultsSources[0]
            : reviewResultsSources,
          leftPanelSavedReportData
        )
      );

      const payload = getABMJSONResultReqPayload(
        convertServerBatchTypeToClient(batchLevel),
        reportIdFromUrl,
        1,
        DEFAULT_PAGE_SIZE,
        getColumnSortingKey(
          paginationSortConfigRef.current.sort,
          convertServerBatchTypeToClient(batchLevel)
        ),
        'asc'
      );
      fetchReportData(payload, convertServerBatchTypeToClient(batchLevel));
    }
  }, [
    reviewResultsSources,
    isLoadingReviewResultsSources,
    isErrorReviewResultsSources,
    reportKey,
  ]);

  useEffect(() => {
    if (
      !isLoadingSavedLeftPanelReport &&
      leftPanelSavedReportData &&
      !isErrorSavedLeftPanelReport
    ) {
      fetchReviewResultsSources();
    } else if (
      leftPanelSavedReportError &&
      isErrorSavedLeftPanelReport &&
      !isLoadingSavedLeftPanelReport
    ) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelSavedReportError as any)?.response?.data?.title ??
          t(stringPath + 'reportConfigAPIError'),
      });
    }
  }, [
    leftPanelSavedReportData,
    isLoadingSavedLeftPanelReport,
    isErrorSavedLeftPanelReport,
    leftPanelSavedReportError,
  ]);

  const payloadForSaveReport = useMemo(() => {
    if (reportConfig && (savedReportData.reportId > 0 || reportIdFromUrl)) {
      return {
        reportId:
          savedReportData.reportId > 0
            ? savedReportData.reportId
            : reportIdFromUrl,
        ...getSaveABMReportDataPayload(
          reportConfig!,
          savedFilterValues,
          savedReportData.batchLevel,
          Array.isArray(reviewResultsSources)
            ? reviewResultsSources[0]
            : reviewResultsSources,
          documentFilterType?.id as string
        ),
      };
    }
    return null;
  }, [
    reportConfig,
    reviewResultsSources,
    documentFilterType,
    savedReportData,
    savedFilterValues,
  ]);

  const [initialActionMenuSources] = useState(() => {
    const stored = sessionStorage.getItem('actionMenuSources');
    return stored ? JSON.parse(stored) : {};
  });

  const actionMenuSources = useMemo(() => {
    if (payloadForSaveReport) {
      return getSourceDetailForAction(
        payloadForSaveReport,
        reportGridData,
        selectedAssignmentIds,
        selectedDocIds,
        isAllDocSelected
      );
    }
    return initialActionMenuSources || {};
  }, [
    reportGridData,
    payloadForSaveReport,
    selectedAssignmentIds,
    selectedDocIds,
    isAllDocSelected,
    initialActionMenuSources,
  ]);

  const sources: SourceItem[] = useMemo(() => {
    if (!reviewResultsSources) return [];
    const src: any = Array.isArray(reviewResultsSources)
      ? reviewResultsSources[0]
      : reviewResultsSources;
    const data: SourceItem[] = [];
    sourcesSectionKeys.forEach((key) => {
      switch (key) {
        case 'SEARCHES':
          data.push({
            id: 'SEARCHES',
            label: t(stringPath + 'searches'),
            data: src?.searchesList ?? [],
          });
          break;
        case 'ASSIGNMENTS':
          data.push({
            id: 'ASSIGNMENTS',
            label: t(stringPath + 'assignments'),
            data: src?.assignmentsList ?? [],
          });
          break;
        case 'FOLDERS':
          data.push({
            id: 'FOLDERS',
            label: t(stringPath + 'folders'),
            data: src?.foldersList ?? [],
          });
          break;
        default:
          break;
      }
    });
    return data;
  }, [reviewResultsSources, t]);

  const intitalSelectedSoruces = useMemo(() => {
    if (isNil(leftPanelSavedReportData)) return {};
    return {
      [leftPanelSavedReportData.selectedSource]:
        leftPanelSavedReportData.selectedSourceID,
    };
  }, [leftPanelSavedReportData]);

  const handleTallyAction = () => {
    const getMasterDocIdPayload = {
      ...actionMenuSources,
      sortColumn: actionMenuSources.SortingColumn,
      sortDirection: actionMenuSources.IsAscending,
    };

    const handleBackgroundSuccess = (data: GetDocumentMasterDocIdResponse) => {
      navigate('/Report/Tally', {
        state: {
          navigationSourceType: SourceTypeCode.ADVBATMGMTRPT,
          source: [data.navigationDocListId],
          sourceName: 'DOCLIST',
        },
      });
    };

    const handleDocIdSuccess = (data: GetDocumentMasterDocIdResponse) => {
      additionMessageToBackground(
        {
          sourceTypeCode: data.source,
          isTallyBulk: true,
          reportID: savedReportData.reportId.toString(),
          destinationContextTypeCode: 'TALLY',
          documentListMasterID: data.navigationDocListId,
        },
        {onSuccess: () => handleBackgroundSuccess(data)}
      );
    };

    const handleMasterDocIdError = (error: any) => {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          error?.response?.data?.title ?? t(stringPath + 'reportAPIError'),
      });
    };

    getDocumentMasterDocId(getMasterDocIdPayload, {
      onSuccess: handleDocIdSuccess,
      onError: handleMasterDocIdError,
    });
  };

  const handleActionMenuItemPick = useCallback(
    (
      action: CustomMenuItem | null,
      _callBackForDoc?: (docId: string, source: string) => void
    ) => {
      if (String(action?.id || '').toLowerCase() === 'exportdata') {
        const getMasterDocIdPayload = {
          ...actionMenuSources,
          sortColumn: actionMenuSources.SortingColumn,
          sortDirection: actionMenuSources.IsAscending,
        };

        const handleExportNavigate = (data: GetDocumentMasterDocIdResponse) => {
          navigate('/Report/ExportData', {
            state: {
              item: {
                navigationSourceType: SourceTypeCode.ADVBATMGMTRPT,
                source: [String(data.navigationDocListId)], // normalize to string
                sourceName: 'DOCLIST',
                sourceDisable: true,
              },
            },
          });
        };

        const handleMasterDocIdError = (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.response?.data?.title ?? t(stringPath + 'reportAPIError'),
          });
        };

        getDocumentMasterDocId(getMasterDocIdPayload, {
          onSuccess: (data: GetDocumentMasterDocIdResponse) => {
            additionMessageToBackground(
              {
                sourceTypeCode: data.source,
                reportID: savedReportData.reportId.toString(),
                destinationContextTypeCode: 'EXPORTDATA',
                documentListMasterID: data.navigationDocListId,
                isTallyBulk: false,
              },
              {
                onSuccess: () => handleExportNavigate(data),
                onError: (error: any) => handleMasterDocIdError(error),
              }
            );
          },
          onError: handleMasterDocIdError,
        });

        return;
      }

      setSelectedActionId(String(action?.id));
      switch (action?.id) {
        case ReportActionMenuOptionTypes.ViewInDocView:
        case ReportActionMenuOptionTypes.ViewInDocViewNewTab: {
          setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
          setShowDocLimitDialog(true);
          break;
        }
        case ReportActionMenuOptionTypes.ViewInDocList:
          setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
          setShowDocLimitDialog(true);
          break;
        case ReportActionMenuOptionTypes.Tally:
          handleTallyAction();
          break;
        default:
          break;
      }
    },
    [actionMenuSources, getDocumentMasterDocId, navigate]
  );

  const [dataAsOf, setDataAsOf] = useState<string>(() =>
    dayjs.utc().format('YYYY/MM/DD HH:mm:ss')
  );

  const handleExportClick = () => {
    const payload = {
      ActionType: 'EXPORTREPORT',
      ReportType: 'ABM001',
      filteration: {
        ...payloadForSaveReport,
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
      },
    };
    batchReportExportToExcel(payload, {
      onSuccess: () => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Success,
          message: t('reports.common.reportExportSuccess'),
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t('reports.common.reportExportError'),
        });
      },
    });
  };

  const docCountSummaryView = useMemo(() => {
    if (
      (savedReportData.reportId || reportIdFromUrl) &&
      abmViewData &&
      isQueryRun
    ) {
      let title = '';
      let actionButtonEnabled = false;
      if (savedReportData.batchLevel === 'Assignment') {
        title = t(stringPath + 'assignment');
        actionButtonEnabled = selectedAssignmentIds.size > 0;
      } else if (savedReportData.batchLevel === 'Document') {
        title = t(stringPath + 'documents');
        actionButtonEnabled = selectedDocIds.size > 0 || isAllDocSelected;
      }
      return (
        <>
          <DocCountSummaryView
            abmViewData={abmViewData}
            bathchLevel={savedReportData.batchLevel}
          />
          <Box className="table-action-header">
            <Typography
              className="typo-text subhead3_size title-label"
              tabIndex={0}
              data-testid={`advanced-batch-management-container-title`}
            >
              {title}
            </Typography>
            <Box className="action-button-container">
              {savedReportData.batchLevel === 'Document' && (
                <>
                  <Typography
                    className="typo-text body3_size filter-label"
                    data-testid={`advanced-batch-management-container-document-path-filter`}
                  >
                    {t(stringPath + 'filter')}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    sizeVariant="medium"
                    dropdownItems={getDocumentListFilterOptions(t)}
                    selectedValues={documentFilterType}
                    onSelectionChange={(value) => {
                      if (value?.id !== documentFilterType.id) {
                        setDocumentFilterType(value as ISelectItem);
                        executeSaveABMReportData(
                          savedFilterValues,
                          value?.id as string
                        );
                      }
                    }}
                    dataTestId="asd-get-document-list-filter-option"
                  />
                </>
              )}
              {actionMenuSources && (
                <ActionMenuWrapper
                  actions={getActionMenuOptions(
                    t,
                    savedReportData.batchLevel,
                    selectedAssignmentIds,
                    selectedDocIds
                  )}
                  isDisabled={!actionButtonEnabled}
                  menuClassName="abm-action-menu"
                  sourceType={SourceTypeCode.ADVBATMGMTRPT}
                  sourceTypeContext={SourceTypeContextCode.ADVBATCHMGTNOC}
                  sourceDetails={actionMenuSources}
                  getDoActionData={getBulkActionExecutePayload}
                  dataTestId="adb-action-menu"
                  getRedistributeData={() =>
                    getRedistributeActionData(
                      savedReportData.batchLevel,
                      reportGridData,
                      selectedAssignmentIds,
                      selectedDocIds
                    )
                  }
                  onRefreshReportList={() => {
                    executeSaveABMReportData(
                      savedFilterValues,
                      savedReportData.batchLevel === 'Document'
                        ? (documentFilterType.id as string)
                        : undefined
                    );
                  }}
                  showToastFromParent={(config) =>
                    setToastMsgConfig({
                      show: true,
                      type: config.type,
                      message: config.message,
                    })
                  }
                  onActionPick={handleActionMenuItemPick}
                />
              )}
            </Box>
          </Box>
        </>
      );
    }
    return undefined;
  }, [
    abmViewData,
    savedReportData,
    isQueryRun,
    documentFilterType,
    t,
    isAllDocSelected,
    selectedDocIds,
    selectedAssignmentIds,
    actionMenuSources,
  ]);

  const rightPanelHeaderConfig = useMemo<RightPanelConfig>(() => {
    return {
      title: t(stringPath + 'title'),
      subTitle: t(stringPath + 'dateAsOf', {
        date: dataAsOf,
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: false,
      showActionButton: false,
      onUploadClick: handleExportClick,
    };
  }, [t, payloadForSaveReport, dataAsOf]);

  const updateSelectedDocsCount = (
    selectedAssignmentIds: Map<string, string[]>,
    selectedDocIds: Map<string, ABMDocResultItemType>
  ): number => {
    let totalDocsCount = 0;

    if (savedReportData?.batchLevel === 'Document') {
      selectedDocIds.forEach((selectedDocId) => {
        const row = reportGridData.find((r) => r.id === selectedDocId.id);

        if (!row) return 0;

        totalDocsCount += row.documentID;
      });
    } else {
      selectedAssignmentIds.forEach((fields, selectedId) => {
        const row = reportGridData.find((r) => r.id === selectedId);
        if (!row) return 0;

        const selectedFields = fields ?? [];

        let valueForId = 0;

        if (selectedFields.length > 1) {
          valueForId = row.inSet ?? 0;
        } else {
          const field = selectedFields[0];
          switch (field) {
            case 'InSet':
              valueForId = row.inSet ?? 0;
              break;
            case 'InSetCompleted':
              valueForId = row.inSetCompleted ?? 0;
              break;
            case 'InSetLeftToDo':
              valueForId = row.inSetLeftToDo ?? 0;
              break;
            default:
              valueForId = 0;
          }
        }

        totalDocsCount += valueForId;
      });
    }

    return totalDocsCount;
  };

  useEffect(() => {
    const totalSelectedDocs = updateSelectedDocsCount(
      selectedAssignmentIds,
      selectedDocIds
    );
    setSelectedDocsCount(totalSelectedDocs);
  }, [selectedAssignmentIds, reportGridData, selectedDocIds]);

  useEffect(() => {
    if (!isNavigating) return;
    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        (navType === SourceTypeCode.DOCVIEW ||
          navType === SourceTypeCode.DOCLIST)
      ) {
        setIsDocViewLoading(false);
        setIsNavigating(false);
        setTimeout(() => {
          navigateToDoc();
        }, 0);
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsDocViewLoading(false);
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t('docExplorer.docDataGrid.navigationErrorToastText'),
      });
      return;
    }
  }, [lastEvent, docListMasterId, isNavigating]);

  const navigateToDoc = () => {
    const isDocViewInNewTab =
      selectedActionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab;
    const isDocList =
      selectedActionId == ReportActionMenuOptionTypes.ViewInDocList;

    if (!isDocList) {
      clearDocViewState();
    }

    dispatch(storeNavigationSourceTypeCode(SourceTypeCode.ADVBATMGMTRPT));
    dispatch(storeDocumentListMasterId(String(docListMasterId)));
    dispatch(storeSourceTypeCode(SourceTypeCode.DOCUMENT));

    setIsDocViewLoading(false);

    if (isDocViewInNewTab) {
      const newWindow = window.open('about:blank', '_blank');
      if (newWindow) {
        newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
      }
    } else if (isDocList) {
      dispatch(setDocListId(Number(docListMasterId)));
      dispatch(
        setDocListBackNavigation({
          label: t('reports.advanceBatchManagement.header'),
          path: 'Review/AdvancedBatchManagementReport',
        })
      );

      sessionStorage.setItem(
        'actionMenuSources',
        JSON.stringify(actionMenuSources) ?? null
      );

      navigate('/Document/DocList');
      clearDocViewState();
    } else {
      navigate('/DocumentViewer/DocView', {replace: true});
    }
  };

  const columns = useMemo<ColumnConfig[]>(() => {
    if (savedReportData.batchLevel === 'Assignment') {
      return assignmentColumns.map((col) => {
        if (
          col.field === 'InSet' ||
          col.field === 'InSetCompleted' ||
          col.field === 'InSetLeftToDo'
        ) {
          return {
            ...col,
            renderCell: (params: GridRenderCellParams) => {
              return (
                <Box className="checkbox-cell-container">
                  <ArrCheckbox
                    checked={
                      selectedAssignmentIds
                        .get(params.row.id)
                        ?.includes(col.field) ?? false
                    }
                    size="small"
                    onChange={() => {
                      const id = params.row.id;
                      const updateSelection = new Map(selectedAssignmentIds);
                      const existingRow: string[] =
                        updateSelection.get(id) ?? [];
                      if (existingRow.includes(col.field))
                        existingRow.splice(existingRow.indexOf(col.field), 1);
                      else existingRow.push(col.field);
                      if (existingRow.length === 0) {
                        updateSelection.delete(id);
                      } else {
                        updateSelection.set(id, existingRow);
                      }
                      setSelectedAssignmentIds(updateSelection);
                    }}
                    data-testId={`${params.row.id}_${col.field}`}
                    aria-label={`${col.field}-checkbox-${params.id}`}
                  />
                  <span
                    data-testid={`advanced-batch-management-cell-value-${col.field}-${params.id}`}
                  >
                    {new Intl.NumberFormat().format(
                      params.row[mapInSetCountKeyToGridField(col.field)] ?? 0
                    )}
                  </span>
                </Box>
              );
            },
          };
        }
        return col;
      });
    } else if (savedReportData.batchLevel === 'Document') {
      return documentColumns;
    }
    return [];
  }, [savedReportData, selectedAssignmentIds]);

  const totalRecordsCount = useMemo(() => {
    switch (savedReportData.batchLevel) {
      case 'Assignment':
        return abmJSONResultData?.totalRecords ?? 0;
      case 'Document':
        return abmDocJSONResultData?.totalDisplayRecords ?? 0;
      default:
        return 0;
    }
  }, [abmJSONResultData, abmDocJSONResultData, savedReportData]);

  const loadReportConfig = useCallback(async () => {
    if (!reportKey) return;
    try {
      const data: Reports = await import(`../../config/${reportKey}.json`);
      initialJsonConfig.current = data;
      return data;
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t(stringPath + 'reportConfigAPIError'),
      });
      console.log('Error loading JSON:', error);
    }
  }, [reportKey, reviewResultsSources]);

  const handleFilterChange = useCallback((values: Record<string, any>) => {
    setReportConfig((prevReportConfig: Reports | null) => {
      if (!prevReportConfig) return prevReportConfig;
      return updateApplyButtonState(prevReportConfig, values);
    });
  }, []);

  const resetStateToInitial = () => {
    setSelectedAssignmentIds(new Map());
    setSelectedDocIds(new Map());
    setUnSelectedDocIds([]);
    setIsAllDocSelected(false);
    setReportGridData([]);
  };

  const fetchReportData = (
    payload: GetABMJSONResultReqPayload | GetABMDocJSONResultReqPayload,
    batchLevel: BatchLevel
  ) => {
    const onSuccess = () => {
      refetchABMSummaryViewData();
      setIsQueryRun(true);
    };
    const onError = (error: any) => {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          error?.response?.data?.title ?? t(stringPath + 'reportAPIError'),
      });
    };
    switch (batchLevel) {
      case 'Assignment':
        getABMJSONResultData(payload as GetABMJSONResultReqPayload, {
          onSuccess,
          onError,
        });
        break;
      case 'Document':
        getABMDocJSONResultData(payload as GetABMDocJSONResultReqPayload, {
          onSuccess,
          onError,
        });
        break;
      default:
        break;
    }
  };

  const fetchABMJSONResult = (
    sortModal: GridSortModel,
    paginationConfig: {pageNumber: number; pageSize: number}
  ) => {
    paginationSortConfigRef.current = {
      pageSize: paginationConfig.pageSize,
      pageNumber: paginationConfig.pageNumber,
      sort: sortModal,
    };
    setPaginationConfig(paginationSortConfigRef.current);
    const payload = getABMJSONResultReqPayload(
      savedReportData.batchLevel,
      savedReportData.reportId,
      paginationConfig.pageNumber,
      paginationConfig.pageSize,
      getColumnSortingKey(sortModal, savedReportData.batchLevel),
      sortModal[0]?.sort ?? 'asc'
    );
    fetchReportData(payload, savedReportData.batchLevel);
  };

  const executeSaveABMReportData = (
    values: Record<string, any>,
    documentFilterType?: string
  ) => {
    const savePayload = getSaveABMReportDataPayload(
      reportConfig!,
      values,
      selectedBatchType.id as BatchLevel,
      Array.isArray(reviewResultsSources)
        ? reviewResultsSources[0]
        : reviewResultsSources,
      documentFilterType
    );
    setIsQueryRun(false);
    resetStateToInitial();
    saveABMReportDataRequest(savePayload, {
      onSuccess: (data) => {
        paginationSortConfigRef.current = {
          pageSize: DEFAULT_PAGE_SIZE,
          pageNumber: 1,
          sort: getDefaultSortingForBatchLevel(selectedBatchType.id),
        };
        setPaginationConfig(paginationSortConfigRef.current);
        sessionStorage.setItem('reportID', data.reportID.toString());
        setSavedReportData({
          reportId: data.reportID,
          batchLevel: selectedBatchType.id as BatchLevel,
          notificationId: data.notificationID,
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t(stringPath + 'runReportFailedMsg'),
        });
      },
    });
  };

  const handleDocSelection = (data: DataGridRowSelectionModel) => {
    const selectedDoc = new Map<string, ABMDocResultItemType>();
    reportGridData.forEach((item: ABMDocResultItemType) => {
      if (data.selectedIds.includes(item.id)) {
        selectedDoc.set(item.id, item);
      }
    });
    data.selectedIds.forEach((id) => {
      if (selectedDocIds.has(id) && !selectedDoc.has(id)) {
        selectedDoc.set(id, selectedDocIds.get(id) as ABMDocResultItemType);
      }
    });
    setSelectedDocIds(selectedDoc);
    setUnSelectedDocIds(data.unselectedIds as string[]);
    setIsAllDocSelected(data.isAllSelected);
  };

  const onContinueDocLimit = useCallback(
    async (totalDocumentToView: number) => {
      setShowDocLimitDialog(false);

      const isDocViewInNewTab =
        selectedActionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab;
      const isDocList =
        selectedActionId == ReportActionMenuOptionTypes.ViewInDocList;
      if (!isDocViewInNewTab) {
        setIsDocViewLoading(true);
      }

      const payload = {
        ...actionMenuSources,
        selectedSourceIds: actionMenuSources.SelectedSourceID,
        navigationDestinationContext: isDocList
          ? SourceTypeCode.DOCLIST
          : SourceTypeCode.DOCVIEW,
        sortColumn: actionMenuSources.SortingColumn,
        sortDirection: actionMenuSources.IsAscending,
      };

      const handleDocIdSuccess = (data: GetDocumentMasterDocIdResponse) => {
        setIsNavigating(true);
        setDocListMasterId(data.navigationDocListId);
        additionMessageToBackground({
          sourceTypeCode: data.source,
          isTallyBulk: true,
          reportID:
            savedReportData.reportId == 0
              ? reportIdFromUrl.toString()
              : savedReportData.reportId.toString(),
          destinationContextTypeCode: isDocList
            ? SourceTypeCode.DOCLIST
            : SourceTypeCode.DOCVIEW,
          documentListMasterID: data.navigationDocListId,
          totalDocumentToView,
        });
      };

      getDocumentMasterDocId(payload, {
        onSuccess: handleDocIdSuccess,
        onError: (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.response?.data?.title ?? t(stringPath + 'reportAPIError'),
          });
        },
      });
    },
    [
      selectedActionId,
      savedReportData,
      additionMessageToBackground,
      getDocumentMasterDocId,
    ]
  );

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitDialog(false);
  }, []);

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));
      setSavedFilterValues(values);
      executeSaveABMReportData(
        values,
        selectedBatchType.id === 'Document'
          ? (documentFilterType.id as string)
          : undefined
      );
    } else if (actionKey === 'reset') {
      isResetExecuted.current = true;
      if (reportConfig && reviewResultsSources)
        setReportConfig(
          mergeApiDataIntoConfig(
            reportConfig,
            Array.isArray(reviewResultsSources)
              ? reviewResultsSources[0]
              : reviewResultsSources
          )
        );
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setSelectedBatchType({
        id: 'Assignment',
        label: t(stringPath + 'assignmentType'),
      });

      setSavedReportData({
        reportId: 0,
        batchLevel: 'Assignment',
        notificationId: 0,
      });
      setDocumentFilterType(getDocumentListFilterOptions(t)[0]);
      paginationSortConfigRef.current = {
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sort: [],
      };
      setPaginationConfig(paginationSortConfigRef.current);
      if (isQueryRun) fetchReviewResultsSources();

      if (reportIdFromUrl !== 0) {
        navigate(location.pathname, {
          replace: true,
          state: {
            isBackgroundReport: false,
          },
        });
      }
    }
  };

  if (
    _.isNil(reportConfig) ||
    _.isNil(reviewResultsSources) ||
    isLoadingReviewResultsSources ||
    isLoadingSavedLeftPanelReport
  )
    return <ArrLoadingSpinner isOpen overlay />;

  return (
    <>
      <StandardReports
        className="adb-container"
        title={
          location?.state?.fromReportLanding
            ? (location?.state?.item?.label ?? '')
            : (reportConfig?.title ?? '')
        }
        infoContent={reportConfig?.infoContent}
        infoModalTitle={reportConfig?.infoModalTitle ?? ''}
        showInfoIcon={reportConfig?.showInfoIcon ?? false}
        reportsConfig={reportConfig?.config as any}
        data={reportGridData}
        mapRows={(data) => data}
        loading={
          isLoadingABMJSONResultData ||
          isLoadingABMDocJSONResultData ||
          isLoadingABMViewData
        }
        selectedOption={selectedBatchType}
        columns={columns}
        sources={sources}
        onAction={handleActionClick}
        onFilterChange={handleFilterChange}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        enableSave={isQueryRun}
        isQueryRun={isQueryRun}
        initialSelectedSources={
          !isResetExecuted.current ? intitalSelectedSoruces : undefined
        }
        validateActionFromParent
        rightHeaderActionOptions={getManageBatchTypedOptions(
          t,
          selectedBatchType.id
        )}
        onRightHeaderActionPick={(value) => {
          setSelectedBatchType({
            id: value.id as BatchLevel,
            label: value.label,
          });
        }}
        saveReportConfig={payloadForSaveReport}
        selectedRightHeaderAction={selectedBatchType}
        optionsCaption={t(stringPath + 'manageBatchAt')}
        customHeaderContent={docCountSummaryView}
        getRowId={(row) => row.id}
        gridConfig={{
          primaryKey: 'id',
          totalRecords: totalRecordsCount,
          className: clsx(
            'adb-report-grid',
            savedReportData.batchLevel === 'Document' &&
              'adb-report-grid-documents'
          ),
          enableCheckbox: savedReportData.batchLevel === 'Document',
          disableVirtualization: false,
          pageSizeOptions: [10, 50, 100, 500],
          disableColumnResize: false,
          itemsPerPage: paginationSortConfigRef.current.pageSize,
          currentPage: paginationSortConfigRef.current.pageNumber,
          sortModel: paginationSortConfigRef.current.sort,
          onSortModelChange: (value) => {
            if (_.isEqual(value, paginationSortConfigRef.current.sort)) return;
            fetchABMJSONResult(value, {
              pageNumber: 1,
              pageSize: paginationSortConfigRef.current.pageSize,
            });
          },
          setItemsPerPage: (value) => {
            fetchABMJSONResult(paginationSortConfigRef.current.sort, {
              pageNumber: 1,
              pageSize: value,
            });
          },
          onPageChange: (pageNumber, pageSize) => {
            if (pageNumber !== paginationSortConfigRef.current.pageNumber) {
              fetchABMJSONResult(paginationSortConfigRef.current.sort, {
                pageNumber,
                pageSize: pageSize ?? paginationSortConfigRef.current.pageSize,
              });
            }
          },
          getRowHeight: () => 'auto',
          entityType: t('common.entries'),
          selectedIds: Array.from(selectedDocIds.keys()),
          unselectedIds: unSelectedDocIds,
          isAllSelected: isAllDocSelected,
          onSelectionStateChange: handleDocSelection,
        }}
        observReportConfigUpdate={false}
      />
      {(isSavingABMReportData || isBatchReportExportToExcelLoading) && (
        <ArrLoadingSpinner isOpen overlay />
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={selectedDocsCount}
        show={showDocLimitDialog}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
      {isDocViewLoading && (
        <ArrLoadingSpinner overlay isOpen={isDocViewLoading} />
      )}
      {_.get(savedReportData, 'reportId', 0) !== 0 &&
        _.get(savedReportData, 'notificationId', 0) !== 0 && (
          <ArrWaitToCompleteModalWrapper
            showClose={false}
            reportId={savedReportData.reportId}
            notificationId={savedReportData.notificationId ?? 0}
            onComplete={(reportId) => {
              if (savedReportData.reportId === reportId) {
                const payload = getABMJSONResultReqPayload(
                  savedReportData.batchLevel,
                  reportId,
                  1,
                  DEFAULT_PAGE_SIZE,
                  getColumnSortingKey(
                    paginationSortConfigRef.current.sort,
                    selectedBatchType.id
                  ),
                  'asc'
                );
                fetchReportData(payload, selectedBatchType.id);
              }
            }}
          />
        )}
    </>
  );
};

export default AdvancedBatchManagementContainer;
